import React, { useEffect, useRef, useState } from 'react';

import { lensTokens } from '~/configs/unifiedAnalyticsTheme';

import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Card, CardContent, IconButton, Stack, TextField, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

interface LensDraftCardProps {
  /** Canvas frame the draft occupies, so the card it becomes lands here too. */
  frame?: { x: number; y: number; w: number; h: number };
  onCancel: () => void;
  /** Opens the one brief → plan → approval flow. The draft deliberately owns
   * no direct query-writing or card-saving path. */
  onStart: (brief: string) => void;
}

/**
 * The draft is the card-shaped composer on the board, rather than a second
 * modal surface. It reserves the destination before the reader describes the
 * question, then hands the brief to the same plan → approval flow used
 * everywhere else.
 */
export function LensDraftCard({ frame, onCancel, onStart }: LensDraftCardProps) {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [brief, setBrief] = useState('');
  const theme = useTheme();
  const tokens = lensTokens(theme);

  // The card is added below the fold on any board of a real size, so it brings
  // itself into view rather than leaving the new destination to be hunted.
  useEffect(() => {
    cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, []);

  const start = () => {
    const trimmed = brief.trim();
    if (trimmed) {
      onStart(trimmed);
    }
  };

  return (
    <Card
      // Placed on the canvas rather than beside it: the frame it occupies is
      // the frame the finished card is saved into, so nothing already on the
      // board moves when the draft is replaced by its answer.
      sx={
        frame
          ? {
              gridColumn: { md: `${frame.x + 1} / span ${frame.w}`, xs: '1 / -1' },
              // The draft lives in the row after the current canvas. Its x/w
              // stay aligned to the canvas grid; y/h belong to the final card
              // plan and must not create empty CSS grid rows here.
              alignSelf: 'start',
              minHeight: 204,
              minWidth: 0,
              overflow: 'hidden',
            }
          : { maxWidth: { md: 520 }, minHeight: 204, width: '100%' }
      }
      variant="outlined"
    >
      <CardContent
        sx={{
          backgroundColor: alpha(tokens.provisionalBg, theme.palette.mode === 'dark' ? 0.52 : 0.62),
          borderLeft: `3px solid ${alpha(tokens.provisionalText, 0.6)}`,
          height: '100%',
        }}
      >
        <Stack spacing={1.25} sx={{ height: '100%' }}>
          <Stack alignItems="flex-start" direction="row" spacing={1}>
            <Box sx={{ flex: 1 }}>
              <Typography color="text.secondary" sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em' }}>
                NEW CARD
              </Typography>
              <Typography sx={{ mt: 0.25 }} variant="subtitle1">
                Describe what this card should show
              </Typography>
            </Box>
            <IconButton aria-label="Discard new card" onClick={onCancel} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
          <TextField
            autoFocus
            fullWidth
            label="What should this card show?"
            minRows={2}
            multiline
            onChange={(event) => setBrief(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
                start();
              }
            }}
            placeholder="e.g. Gross sales today by customer market"
            sx={{ '& .MuiOutlinedInput-root': { backgroundColor: tokens.surface } }}
            value={brief}
          />
          <Stack alignItems="center" direction="row" justifyContent="space-between" spacing={1}>
            <Button onClick={onCancel} size="small">
              Discard
            </Button>
            <Button
              disabled={!brief.trim()}
              onClick={start}
              size="small"
              sx={{
                backgroundColor: tokens.accent,
                color: tokens.accentContrast,
                '&:hover': { backgroundColor: alpha(tokens.accent, 0.88) },
              }}
              variant="contained"
            >
              Review plan
            </Button>
          </Stack>
        </Stack>
      </CardContent>
      <Box ref={cardRef} />
    </Card>
  );
}

export default LensDraftCard;
